import * as React from 'react'
import dayjs, { Dayjs } from 'dayjs'

import { Calendar } from 'src/components/ui/calendar'
import type { Matcher } from 'src/components/ui/calendar'
import { cn } from 'src/lib/utils'

import { PickerMode, ShowTimeConfig } from './format'
import { localeDataFor, withLocale } from './locale'

// Panel bodies for the `src/antd/DatePicker` seam's shadcn half.
//
// The DAY grid is `react-day-picker` (through the shadcn `Calendar`
// primitive). Everything else — the header, the month/quarter/year grids and
// the time columns — is rendered here, because react-day-picker only does
// days. Every label and every boundary is computed with dayjs, so the app
// keeps ONE date library: the Danish and Finnish month names, weekday names,
// first-day-of-week and week numbers all come from the same place they always
// did (`src/App/DayjsI18n`), NOT from react-day-picker's bundled date-fns
// locale data, which nothing here ever consults.
//
// DOM contract: `data-slot="picker-*"`, never `.ant-picker-*`. On this branch
// that is not a preference but a requirement: antd is still installed and its
// picker stylesheet is UNLAYERED, so it outranks every Tailwind utility — and
// cssinjs never removes a sheet it has injected, so the moment anything on the
// page has rendered an antd picker (a `TimePicker`, which is deliberately NOT
// ported, or any picker at all with the toggle off) those rules are live.
// Re-emitting the class names would hand antd's CSS the argument. Live
// stylesheets that reached into `.ant-picker-*` grew a `[data-slot='picker-*']`
// selector beside it instead — see `src/storybook/components/DatePickerBase`.

export type PanelMode = 'date' | 'week' | 'month' | 'quarter' | 'year' | 'time'

/** The panel a picker mode opens on. */
export const initialPanelMode = (picker: PickerMode): PanelMode =>
  picker === 'week' ? 'date' : (picker as PanelMode)

export type CellRender = (
  current: Dayjs,
  info: { type: PanelMode; today: Dayjs; range?: 'start' | 'end' }
) => React.ReactNode

type SharedBodyProps = {
  viewDate: Dayjs
  localeName?: string
  disabledDate?: (date: Dayjs) => boolean
  cellRender?: CellRender
  rangeSide?: 'start' | 'end'
}

const headerButtonClass =
  'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40'

const viewButtonClass =
  'inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground'

/**
 * Panel header: `«  ‹  <view buttons>  ›  »`.
 *
 * A range panel hides the arrows that belong to the OTHER side (antd does the
 * same), which is why `showPrev`/`showNext` are separate.
 */
export const PanelHeader = ({
  showPrev = true,
  showNext = true,
  onSuperPrev,
  onPrev,
  onNext,
  onSuperNext,
  children,
  labels,
}: {
  showPrev?: boolean
  showNext?: boolean
  onSuperPrev?: () => void
  onPrev?: () => void
  onNext?: () => void
  onSuperNext?: () => void
  children: React.ReactNode
  labels: {
    superPrev: string
    prev: string
    next: string
    superNext: string
  }
}) => (
  <div
    data-slot="picker-header"
    className="flex items-center justify-between gap-1 border-b px-2 py-2"
  >
    <div className="flex items-center gap-0.5">
      {showPrev && onSuperPrev && (
        <button
          type="button"
          data-slot="picker-header-super-prev"
          aria-label={labels.superPrev}
          className={headerButtonClass}
          onClick={onSuperPrev}
        >
          «
        </button>
      )}
      {showPrev && onPrev && (
        <button
          type="button"
          data-slot="picker-header-prev"
          aria-label={labels.prev}
          className={headerButtonClass}
          onClick={onPrev}
        >
          ‹
        </button>
      )}
    </div>
    <div data-slot="picker-header-view" className="flex items-center gap-1">
      {children}
    </div>
    <div className="flex items-center gap-0.5">
      {showNext && onNext && (
        <button
          type="button"
          data-slot="picker-header-next"
          aria-label={labels.next}
          className={headerButtonClass}
          onClick={onNext}
        >
          ›
        </button>
      )}
      {showNext && onSuperNext && (
        <button
          type="button"
          data-slot="picker-header-super-next"
          aria-label={labels.superNext}
          className={headerButtonClass}
          onClick={onSuperNext}
        >
          »
        </button>
      )}
    </div>
  </div>
)

export const HeaderViewButton = ({
  slot,
  onClick,
  children,
}: {
  slot: string
  onClick?: () => void
  children: React.ReactNode
}) =>
  onClick ? (
    <button
      type="button"
      data-slot={slot}
      className={viewButtonClass}
      onClick={onClick}
    >
      {children}
    </button>
  ) : (
    <span data-slot={slot} className="px-1.5 py-0.5 text-sm font-medium">
      {children}
    </span>
  )

/* ------------------------------------------------------------------ dates */

/**
 * The day grid.
 *
 * `selected` drives react-day-picker's range modifiers, so the two-panel
 * range keeps start / middle / end highlighting even though each month is its
 * own `Calendar` instance (antd renders two panels too, and linking them this
 * way is what lets the left header own "previous" and the right header "next").
 */
export const DateBody = ({
  viewDate,
  localeName,
  disabledDate,
  cellRender,
  rangeSide,
  selected,
  rangeSelection,
  onPick,
  showWeekNumber,
  weekSelected,
}: SharedBodyProps & {
  selected?: Dayjs | null
  rangeSelection?: { from?: Dayjs | null; to?: Dayjs | null }
  onPick: (date: Dayjs) => void
  showWeekNumber?: boolean
  /** `picker="week"`: highlight the whole week the value falls in. */
  weekSelected?: Dayjs | null
}) => {
  const today = dayjs()
  const firstDayOfWeek = localeDataFor(localeName).firstDayOfWeek() as
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6

  const disabledMatcher = React.useMemo<Matcher | undefined>(
    () =>
      disabledDate
        ? (date: Date) => disabledDate(withLocale(dayjs(date), localeName))
        : undefined,
    [disabledDate, localeName]
  )

  const weekRange = React.useMemo(() => {
    if (!weekSelected) return undefined
    const base = withLocale(weekSelected, localeName)
    return { from: base.startOf('week'), to: base.endOf('week') }
  }, [weekSelected, localeName])

  const effectiveRange = weekRange ?? rangeSelection

  const commonProps = {
    month: viewDate.startOf('month').toDate(),
    // Navigation lives in our own header; react-day-picker's nav and caption
    // are hidden so both panels of a range share one header language.
    onMonthChange: () => undefined,
    weekStartsOn: firstDayOfWeek,
    showWeekNumber,
    disabled: disabledMatcher,
    formatters: {
      formatWeekdayName: (date: Date) =>
        withLocale(dayjs(date), localeName).format('dd'),
      formatDay: (date: Date) =>
        withLocale(dayjs(date), localeName).format('D'),
      formatCaption: (date: Date) =>
        withLocale(dayjs(date), localeName).format('MMMM YYYY'),
    },
    components: {
      Nav: () => null,
      MonthCaption: () => null,
      Day: ({
        day,
        modifiers,
        ...rest
      }: {
        day: { date: Date }
        modifiers: Record<string, boolean | undefined>
      } & React.HTMLAttributes<HTMLTableCellElement>) => {
        const current = withLocale(dayjs(day.date), localeName)
        return (
          <td
            {...(rest as React.HTMLAttributes<HTMLTableCellElement>)}
            data-slot="picker-cell"
            title={current.format('YYYY-MM-DD')}
            // antd put `title="YYYY-MM-DD"` on the day CELL and handled the
            // click there; react-day-picker puts the handler on the button
            // inside. Both are kept: the title so `td[title=…]` still
            // addresses a day (existing tests and any DOM-coupled code rely on
            // it), and a cell-level click that forwards to the same pick so a
            // click landing on the cell padding is not swallowed. The
            // `target === currentTarget` guard is what stops the button's own
            // click from double-firing as it bubbles.
            onClick={(event) => {
              if (event.target !== event.currentTarget) return
              if (modifiers.disabled || modifiers.hidden) return
              onPick(current)
            }}
          />
        )
      },
      DayButton: ({
        day,
        modifiers,
        children,
        className,
        ...rest
      }: {
        day: { date: Date }
        modifiers: Record<string, boolean | undefined>
      } & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
        const current = withLocale(dayjs(day.date), localeName)
        const custom = cellRender?.(current, {
          type: 'date',
          today,
          range: rangeSide,
        })
        return (
          <button
            type="button"
            {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            data-slot="picker-cell-button"
            className={cn(
              // `min-w-(--cell-size)` is load-bearing and is the ONE thing the
              // reference implementation's day button is missing. The grid is
              // a real `<table>`, so with no minimum the columns size to their
              // digits: cells came out 17px wide, the numbers ran together
              // ("10111213141516"), and the two panels of a range disagreed
              // about their own width (192px vs 217px) because each month's
              // digits differ. Upstream shadcn puts it on `CalendarDayButton`,
              // which this seam replaces — so the seam has to carry it. Browser
              // -verified; jsdom runs no layout and cannot see any of this.
              'flex aspect-square w-full min-w-(--cell-size) items-center justify-center rounded-md text-sm font-normal transition-colors hover:bg-accent hover:text-accent-foreground',
              modifiers.selected &&
                'bg-primary text-primary-foreground hover:bg-primary',
              modifiers.range_middle &&
                'rounded-none bg-accent text-accent-foreground hover:bg-accent',
              modifiers.today &&
                !modifiers.selected &&
                'ring-1 ring-inset ring-primary',
              modifiers.outside && 'text-muted-foreground opacity-50',
              className
            )}
          >
            {custom ?? (
              <span data-slot="picker-cell-inner">{current.format('D')}</span>
            )}
          </button>
        )
      },
      WeekNumber: ({
        week,
        ...rest
      }: {
        week: { days: { date: Date }[] }
      } & React.ThHTMLAttributes<HTMLTableCellElement>) => (
        <td
          {...(rest as React.ThHTMLAttributes<HTMLTableCellElement>)}
          data-slot="picker-week-number"
        >
          <div className="flex size-8 items-center justify-center text-xs text-muted-foreground">
            {withLocale(dayjs(week.days[0]?.date), localeName)
              .endOf('week')
              .isoWeek()}
          </div>
        </td>
      ),
    },
  }

  const handleSelect = (_unused: unknown, triggerDate: Date) => {
    onPick(withLocale(dayjs(triggerDate), localeName))
  }

  return (
    <div data-slot="picker-content">
      {effectiveRange ? (
        <Calendar
          {...(commonProps as $TSFixMe)}
          mode="range"
          selected={{
            from: effectiveRange.from?.toDate(),
            to: effectiveRange.to?.toDate(),
          }}
          onSelect={handleSelect as $TSFixMe}
        />
      ) : (
        <Calendar
          {...(commonProps as $TSFixMe)}
          mode="single"
          selected={selected ? selected.toDate() : undefined}
          onSelect={handleSelect as $TSFixMe}
        />
      )}
    </div>
  )
}

/* ------------------------------------------ month / quarter / year grids */

const gridCellClass = (selected: boolean, disabled: boolean) =>
  cn(
    'flex h-8 items-center justify-center rounded-md text-sm transition-colors',
    disabled
      ? 'pointer-events-none text-muted-foreground opacity-50'
      : 'hover:bg-accent hover:text-accent-foreground',
    selected && 'bg-primary text-primary-foreground hover:bg-primary'
  )

const GridBody = ({
  slot,
  columns,
  cells,
}: {
  slot: string
  columns: number
  cells: {
    key: string
    label: React.ReactNode
    title: string
    selected: boolean
    disabled: boolean
    onClick: () => void
  }[]
}) => (
  <div
    data-slot={slot}
    className="grid gap-1 p-2"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {cells.map((cell) => (
      <button
        key={cell.key}
        type="button"
        data-slot="picker-cell"
        title={cell.title}
        aria-disabled={cell.disabled || undefined}
        aria-pressed={cell.selected}
        className={gridCellClass(cell.selected, cell.disabled)}
        onClick={cell.disabled ? undefined : cell.onClick}
      >
        <span data-slot="picker-cell-inner">{cell.label}</span>
      </button>
    ))}
  </div>
)

export const MonthBody = ({
  viewDate,
  localeName,
  disabledDate,
  cellRender,
  rangeSide,
  selected,
  onPick,
}: SharedBodyProps & {
  selected?: Dayjs | null
  onPick: (date: Dayjs) => void
}) => {
  const today = dayjs()
  const months = localeDataFor(localeName).monthsShort()
  return (
    <GridBody
      slot="picker-month-content"
      columns={3}
      cells={months.map((label, index) => {
        const cellDate = viewDate.month(index).startOf('month')
        return {
          key: String(index),
          label:
            cellRender?.(cellDate, {
              type: 'month',
              today,
              range: rangeSide,
            }) ?? label,
          title: withLocale(cellDate, localeName).format('YYYY-MM'),
          selected: Boolean(selected && selected.isSame(cellDate, 'month')),
          disabled: Boolean(disabledDate?.(cellDate)),
          onClick: () => onPick(cellDate),
        }
      })}
    />
  )
}

export const QuarterBody = ({
  viewDate,
  localeName,
  disabledDate,
  cellRender,
  rangeSide,
  selected,
  onPick,
}: SharedBodyProps & {
  selected?: Dayjs | null
  onPick: (date: Dayjs) => void
}) => {
  const today = dayjs()
  return (
    <GridBody
      slot="picker-quarter-content"
      columns={4}
      cells={[0, 1, 2, 3].map((index) => {
        const cellDate = viewDate.month(index * 3).startOf('month')
        return {
          key: String(index),
          label:
            cellRender?.(cellDate, {
              type: 'quarter',
              today,
              range: rangeSide,
            }) ?? `Q${index + 1}`,
          title: withLocale(cellDate, localeName).format('YYYY-[Q]Q'),
          selected: Boolean(
            selected &&
              selected.isSame(cellDate, 'year') &&
              selected.quarter() === index + 1
          ),
          disabled: Boolean(disabledDate?.(cellDate)),
          onClick: () => onPick(cellDate),
        }
      })}
    />
  )
}

/** The decade the year panel shows (antd renders 10 years plus a spill cell). */
export const decadeStart = (viewDate: Dayjs) =>
  Math.floor(viewDate.year() / 10) * 10

export const YearBody = ({
  viewDate,
  localeName,
  disabledDate,
  cellRender,
  rangeSide,
  selected,
  onPick,
}: SharedBodyProps & {
  selected?: Dayjs | null
  onPick: (date: Dayjs) => void
}) => {
  const today = dayjs()
  const start = decadeStart(viewDate)
  return (
    <GridBody
      slot="picker-year-content"
      columns={3}
      cells={Array.from({ length: 12 }, (_, offset) => {
        const year = start - 1 + offset
        const cellDate = viewDate.year(year).startOf('year')
        return {
          key: String(year),
          label:
            cellRender?.(cellDate, { type: 'year', today, range: rangeSide }) ??
            year,
          title: withLocale(cellDate, localeName).format('YYYY'),
          selected: Boolean(selected && selected.year() === year),
          disabled: Boolean(disabledDate?.(cellDate)),
          onClick: () => onPick(cellDate),
        }
      })}
    />
  )
}

/* -------------------------------------------------------------- time cols */

export type DisabledTimeResult = {
  disabledHours?: () => number[]
  disabledMinutes?: (hour: number) => number[]
  disabledSeconds?: (hour: number, minute: number) => number[]
}

const range = (count: number, step: number) =>
  Array.from({ length: Math.ceil(count / step) }, (_, i) => i * step)

const TimeColumn = ({
  slot,
  values,
  active,
  disabledValues,
  hideDisabled,
  format,
  onSelect,
}: {
  slot: string
  values: number[]
  active: number
  disabledValues: number[]
  hideDisabled?: boolean
  format: (value: number) => string
  onSelect: (value: number) => void
}) => {
  const visible = hideDisabled
    ? values.filter((value) => !disabledValues.includes(value))
    : values
  const ulRef = React.useRef<HTMLUListElement>(null)

  /* antd's time panel scrolls each column to its active cell — on open and on
     every change — or a picker opened at 14:30 shows hours 00-07 and the user
     has to hunt for both the current value and the target (rc-picker's
     `TimePanelBody/useScrollTo`, which lands the cell at the top of the
     column). The retry is what rc-picker needs it for too: the panel is
     portalled, so on the first pass it has no layout yet and every `offsetTop`
     reads 0 — scrolling in that tick silently no-ops. */
  React.useEffect(() => {
    let frame = 0
    let attempts = 0
    const sync = () => {
      const ul = ulRef.current
      if (!ul) return
      const target = ul.querySelector<HTMLElement>(`[data-value="${active}"]`)
      const first = ul.querySelector('li')
      if (!target || !first) return
      const top = target.offsetTop - first.offsetTop
      if (top === 0 && target !== first && attempts < 5) {
        attempts += 1
        frame = requestAnimationFrame(sync)
        return
      }
      ul.scrollTop = top
    }
    sync()
    return () => cancelAnimationFrame(frame)
  }, [active, visible.length])

  return (
    <ul
      ref={ulRef}
      data-slot={slot}
      className="m-0 flex h-[224px] flex-col overflow-y-auto border-l px-1 py-1 first:border-l-0"
    >
      {visible.map((value) => {
        const disabled = disabledValues.includes(value)
        return (
          <li key={value} data-value={value}>
            <button
              type="button"
              data-slot="picker-time-cell"
              aria-pressed={value === active}
              aria-disabled={disabled || undefined}
              className={cn(
                'w-12 rounded-md px-2 py-1 text-center text-sm transition-colors',
                disabled
                  ? 'pointer-events-none text-muted-foreground opacity-50'
                  : 'hover:bg-accent hover:text-accent-foreground',
                value === active && 'bg-accent font-medium'
              )}
              onClick={disabled ? undefined : () => onSelect(value)}
            >
              {format(value)}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

const pad = (value: number) => String(value).padStart(2, '0')

export const TimeBody = ({
  value,
  config,
  disabledTime,
  onChange,
}: {
  value: Dayjs
  config: ShowTimeConfig
  disabledTime?: (date: Dayjs) => DisabledTimeResult
  onChange: (next: Dayjs) => void
}) => {
  // Which columns a `showTime` panel offers follows the time mask actually in
  // use, so the input can never display a unit the panel cannot edit
  // (`showTime` resolves to `HH:mm:ss` and must offer seconds). Explicit
  // showHour/showMinute/showSecond still win.
  const mask = config.format
  const showHour = config.showHour ?? (mask ? /[hH]/.test(mask) : true)
  const showMinute = config.showMinute ?? (mask ? /m/.test(mask) : true)
  const showSecond = config.showSecond ?? (mask ? /s/.test(mask) : true)
  const hourStep = config.hourStep ?? 1
  const minuteStep = config.minuteStep ?? 1
  const secondStep = config.secondStep ?? 1

  const disabled = disabledTime?.(value) ?? {}
  const disabledHours = disabled.disabledHours?.() ?? []
  const disabledMinutes = disabled.disabledMinutes?.(value.hour()) ?? []
  const disabledSeconds =
    disabled.disabledSeconds?.(value.hour(), value.minute()) ?? []

  return (
    <div
      data-slot="picker-time-panel"
      className="flex border-l"
      role="group"
      aria-label="time"
    >
      {showHour && (
        <TimeColumn
          slot="picker-time-column-hour"
          values={range(24, hourStep)}
          active={value.hour()}
          disabledValues={disabledHours}
          hideDisabled={config.hideDisabledOptions}
          format={pad}
          onSelect={(hour) => onChange(value.hour(hour))}
        />
      )}
      {showMinute && (
        <TimeColumn
          slot="picker-time-column-minute"
          values={range(60, minuteStep)}
          active={value.minute()}
          disabledValues={disabledMinutes}
          hideDisabled={config.hideDisabledOptions}
          format={pad}
          onSelect={(minute) => onChange(value.minute(minute))}
        />
      )}
      {showSecond && (
        <TimeColumn
          slot="picker-time-column-second"
          values={range(60, secondStep)}
          active={value.second()}
          disabledValues={disabledSeconds}
          hideDisabled={config.hideDisabledOptions}
          format={pad}
          onSelect={(second) => onChange(value.second(second))}
        />
      )}
    </div>
  )
}
